import { Actor, Canvas, Random, vec } from 'excalibur';
import { ThemePaletteKey, getTheme } from '../themes/theme';
import { SEEDS } from '../utils/constants';
import { css, sketchStroke, wobblyArcPath, wobblyLinePath } from '../utils/sketch';
import { HandLabel } from './label';

export interface SketchButtonOptions {
  name: string;
  text: string;
  width: number;
  height: number;
  accentTone: ThemePaletteKey;
  fontSize?: number;
  onPress: () => void;
}

export class SketchButton extends Actor {
  private readonly buttonWidth: number;
  private readonly buttonHeight: number;
  private readonly label: HandLabel;
  private readonly buttonCanvas: Canvas;

  constructor(options: SketchButtonOptions) {
    const pad = 18;
    const canvas = new Canvas({
      width: options.width + pad * 2,
      height: options.height + pad * 2,
      cache: true,
      draw: (ctx) => paintButton(ctx, options, pad),
    });
    super({
      name: options.name,
      graphic: canvas,
    });
    this.buttonCanvas = canvas;
    this.buttonWidth = options.width;
    this.buttonHeight = options.height;
    this.label = new HandLabel({
      name: `${options.name}-label`,
      text: options.text,
      pos: vec(0, 3),
      size: options.fontSize === undefined ? 44 : options.fontSize,
      tone: 'ink',
    });
    this.addChild(this.label);
    this.on('pointerdown', () => {
      options.onPress();
      this.scale = vec(0.95, 0.95);
    });
    this.on('pointerup', () => {
      this.scale = vec(1, 1);
    });
    this.on('pointerenter', () => {
      this.scale = vec(1.04, 1.04);
    });
    this.on('pointerleave', () => {
      this.scale = vec(1, 1);
    });
  }

  setText(text: string): void {
    this.label.text = text;
  }

  setVisible(visible: boolean): void {
    this.graphics.isVisible = visible;
    this.label.graphics.isVisible = visible;
    for (const child of this.children) {
      if (child instanceof Actor) {
        child.graphics.isVisible = visible;
      }
    }
  }

  isPointInside(x: number, y: number): boolean {
    return (
      Math.abs(x - this.pos.x) <= this.buttonWidth / 2 + 8 &&
      Math.abs(y - this.pos.y) <= this.buttonHeight / 2 + 8
    );
  }

  refreshTheme(): void {
    this.buttonCanvas.flagDirty();
    this.label.refreshTheme();
  }
}

function paintButton(ctx: CanvasRenderingContext2D, options: SketchButtonOptions, pad: number): void {
  const rng = new Random(SEEDS.button + options.width + options.text.length);
  const palette = getTheme().palette;
  const x = pad;
  const y = pad;
  const width = options.width;
  const height = options.height;
  const radius = Math.min(40, height * 0.42);

  ctx.save();
  ctx.fillStyle = css(palette[options.accentTone], 0.28);
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x + 5, y + 5, width - 10, height - 10, radius);
  } else {
    ctx.rect(x + 5, y + 5, width - 10, height - 10);
  }
  ctx.fill();
  ctx.restore();

  const edges: Array<(c: CanvasRenderingContext2D) => void> = [
    (c) => wobblyLinePath(c, x + radius, y, x + width - radius, y, rng, 1.6, 10),
    (c) => wobblyLinePath(c, x + width, y + radius, x + width, y + height - radius, rng, 1.6, 6),
    (c) => wobblyLinePath(c, x + width - radius, y + height, x + radius, y + height, rng, 1.6, 10),
    (c) => wobblyLinePath(c, x, y + height - radius, x, y + radius, rng, 1.6, 6),
    (c) => wobblyArcPath(c, x + width - radius, y + radius, radius, -Math.PI / 2, 0, rng, 1.3, 8),
    (c) => wobblyArcPath(c, x + width - radius, y + height - radius, radius, 0, Math.PI / 2, rng, 1.3, 8),
    (c) => wobblyArcPath(c, x + radius, y + height - radius, radius, Math.PI / 2, Math.PI, rng, 1.3, 8),
    (c) => wobblyArcPath(c, x + radius, y + radius, radius, Math.PI, Math.PI * 1.5, rng, 1.3, 8),
  ];
  for (const edge of edges) {
    sketchStroke(ctx, rng, { color: palette.ink, width: 3.4, alpha: 0.85 }, edge, 2, 1.6);
  }
}
